import { fabric } from 'fabric';
import { TemplateType, isPlaceholderKeyValidFor } from '../template-type';
import { missingFieldsFor } from './placeholder-key-registry';
import { fitTextToBox } from './text-fit';
import type {
  PlaceholderCornerRadii,
  PlaceholderData,
  PlaceholderGeometry,
  PlaceholderImageData,
  PlaceholderTextAlign,
  PlaceholderTextData,
} from './types';

/**
 * Optional rendering context. When `templateType` is provided, the factory
 * additionally renders a placeholder with the warning styling if its key is
 * not allowed for that template type. When omitted, the factory only flags
 * data-level problems (missing teamNumber, mismatched kind/key) — same as before.
 */
export interface PlaceholderRenderContext {
  templateType?: TemplateType;
}

const OUTLINE_COLOR = 'rgba(99, 102, 241, 0.9)';
const WARNING_OUTLINE_COLOR = 'rgba(220, 38, 38, 0.9)';
const WARNING_FILL_COLOR = 'rgba(220, 38, 38, 0.08)';
const FILL_COLOR = 'rgba(99, 102, 241, 0.06)';
const BADGE_BG = '#6366f1';
const BADGE_FG = '#ffffff';
const LABEL_COLOR = '#4338ca';
const DASH_ARRAY = [8, 4];
const STROKE_WIDTH = 2;
const BADGE_HEIGHT = 20;
const BADGE_PADDING_X = 6;
const LABEL_FONT_SIZE = 12;
const HINT_COLOR = 'rgba(67, 56, 202, 0.55)';

/**
 * Default corner radius applied to an image placeholder box when the author has
 * not set `data.cornerRadius`. The box is rendered with this rounding, so the
 * inspector must surface the same value as its initial field state — otherwise
 * the Radius field reads empty while the corners are visibly rounded.
 */
export const DEFAULT_IMAGE_CORNER_RADIUS = 4;

const ZERO_RADII: PlaceholderCornerRadii = { tl: 0, tr: 0, br: 0, bl: 0 };

/**
 * Resolves the four corner radii for an image placeholder. `data.cornerRadii`
 * (per-corner) wins when present; otherwise every corner uses the uniform
 * `data.cornerRadius` (defaulting to `DEFAULT_IMAGE_CORNER_RADIUS`). Keeping
 * this in one place makes the visual and the inspector read from the same rule.
 */
export function resolveImageCornerRadii(data: PlaceholderImageData): PlaceholderCornerRadii {
  if (data.cornerRadii) return data.cornerRadii;
  const uniform = data.cornerRadius ?? DEFAULT_IMAGE_CORNER_RADIUS;
  return { tl: uniform, tr: uniform, br: uniform, bl: uniform };
}

function isUniformRadii(r: PlaceholderCornerRadii): boolean {
  return r.tl === r.tr && r.tr === r.br && r.br === r.bl;
}

const roundCoord = (x: number) => Math.round(x * 1000) / 1000;

/**
 * SVG path `d` for a `width`×`height` rectangle (top-left at 0,0) with four
 * independent corner radii. Each radius is clamped to `min(width,height)/2` so
 * adjacent rounded corners never cross. Used when the corners differ — the
 * uniform case stays a plain `fabric.Rect` with `rx`/`ry`.
 */
export function roundedRectPath(width: number, height: number, r: PlaceholderCornerRadii): string {
  const max = Math.min(width, height) / 2;
  const clamp = (v: number) => roundCoord(Math.max(0, Math.min(v, max)));
  const tl = clamp(r.tl);
  const tr = clamp(r.tr);
  const br = clamp(r.br);
  const bl = clamp(r.bl);
  const w = roundCoord(width);
  const h = roundCoord(height);
  return [
    `M ${tl} 0`,
    `L ${roundCoord(width - tr)} 0`,
    `Q ${w} 0 ${w} ${tr}`,
    `L ${w} ${roundCoord(height - br)}`,
    `Q ${w} ${h} ${roundCoord(width - br)} ${h}`,
    `L ${bl} ${h}`,
    `Q 0 ${h} 0 ${roundCoord(height - bl)}`,
    `L 0 ${tl}`,
    `Q 0 0 ${tl} 0`,
    'Z',
  ].join(' ');
}

/**
 * Builds a rounded rectangle as either a `fabric.Rect` (when all four radii are
 * equal — the cheap, battle-tested uniform path, byte-identical to the prior
 * behavior) or a `fabric.Path` (when corners differ). Origin/position props are
 * passed through in `options`, so the same call works for the top-left-anchored
 * background box and the center-anchored group clipPath alike.
 */
function roundedRectShape(
  width: number,
  height: number,
  radii: PlaceholderCornerRadii,
  options: fabric.IObjectOptions,
): fabric.Object {
  if (isUniformRadii(radii)) {
    return new fabric.Rect({ ...options, width, height, rx: radii.tl, ry: radii.tl });
  }
  return new fabric.Path(roundedRectPath(width, height, radii), options);
}

/**
 * Absolute (canvas-unit) floor a placeholder box is rendered at. Kept at 1 so
 * the author can scale a placeholder down to a single pixel; the bordered box
 * stays a legible mark at that size (see `selectDetailLevel` → `'minimal'`).
 */
const MIN_RENDER_SIZE = 1;

const COMMON_GROUP_PROPS: Partial<fabric.IGroupOptions> = {
  originX: 'left',
  originY: 'top',
  hasRotatingPoint: true,
  objectCaching: false,
  subTargetCheck: false,
  lockUniScaling: false,
};

export function createPlaceholder(
  data: PlaceholderData,
  geometry: PlaceholderGeometry,
  context?: PlaceholderRenderContext,
): fabric.Group {
  if (data.kind === 'placeholder-image') {
    return createImagePlaceholder(data, geometry, context);
  }
  return createTextPlaceholder(data, geometry, context);
}

export function createImagePlaceholder(
  data: PlaceholderImageData,
  geometry: PlaceholderGeometry,
  context?: PlaceholderRenderContext,
): fabric.Group {
  const width = Math.max(MIN_RENDER_SIZE, geometry.width);
  const height = Math.max(MIN_RENDER_SIZE, geometry.height);
  if (data.previewMode) {
    return createImagePreviewLoadingGroup(data, geometry, width, height);
  }
  const isMissing = shouldRenderAsInvalid(data, context);
  const appearance = resolveImageAppearance(data, isMissing);
  const children: fabric.Object[] = [backgroundRect(width, height, isMissing, appearance)];
  appendDecor(children, data, 'IMG', 'IMAGE', width, height);
  return assembleGroup(children, data, geometry, width, height);
}

export function createTextPlaceholder(
  data: PlaceholderTextData,
  geometry: PlaceholderGeometry,
  context?: PlaceholderRenderContext,
): fabric.Group {
  const width = Math.max(MIN_RENDER_SIZE, geometry.width);
  const height = Math.max(MIN_RENDER_SIZE, geometry.height);
  if (data.resolvedText !== undefined || data.previewMode) {
    return createTextPreviewGroup(data, geometry, width, height);
  }
  const isMissing = shouldRenderAsInvalid(data, context);
  const children: fabric.Object[] = [
    backgroundRect(width, height, isMissing, defaultPlaceholderAppearance(isMissing)),
  ];
  appendDecor(children, data, 'T', 'TEXT', width, height);
  return assembleGroup(children, data, geometry, width, height);
}

export type PlaceholderDetailLevel = 'full' | 'compact' | 'minimal';

/**
 * Chooses how much placeholder chrome fits at the given absolute (canvas-unit)
 * size. The badge is a fixed 20px-tall box and the identity label is stacked
 * beneath it at a fixed 12px — they only read correctly in a reasonably large
 * box. Cramming them into a small one made the children overlap and spill past
 * the rect (the group clipPath crops but does not lay out), which is the
 * "crooked rendering" this guards against.
 *
 * - `full`    — badge + identity label + center hint (normal placeholder sizes)
 * - `compact` — a single centered, size-fitted kind hint (`"TEXT"` / `"IMAGE"`)
 * - `minimal` — just the bordered box; stays a legible mark down to 1×1
 */
export function selectDetailLevel(width: number, height: number): PlaceholderDetailLevel {
  if (width >= 72 && height >= 56) return 'full';
  if (width >= 32 && height >= 24) return 'compact';
  return 'minimal';
}

/**
 * Appends the size-appropriate decorative children for a placeholder box on top
 * of the already-pushed background rect. See `selectDetailLevel` for the tiers.
 */
function appendDecor(
  children: fabric.Object[],
  data: PlaceholderData,
  badgeText: string,
  hintText: string,
  width: number,
  height: number,
): void {
  const level = selectDetailLevel(width, height);
  if (level === 'full') {
    children.push(...badge(badgeText, width), labelText(data, width), centerHint(hintText, width, height));
  } else if (level === 'compact') {
    children.push(compactHint(hintText, width, height));
  }
  // 'minimal' — background rect only.
}

/**
 * Whether the placeholder should render with the warning (red) styling.
 * Exported so tests can verify the rule without instantiating Fabric. True if
 * either (a) required data fields are missing, or (b) the key isn't allowed
 * for the active template type.
 */
export function shouldRenderAsInvalid(
  data: PlaceholderData,
  context: PlaceholderRenderContext | undefined,
): boolean {
  if (missingFieldsFor(data, context?.templateType).length > 0) return true;
  if (context?.templateType && !isPlaceholderKeyValidFor(context.templateType, data.placeholderKey)) return true;
  return false;
}

function createTextPreviewGroup(
  data: PlaceholderTextData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
): fabric.Group {
  const display = data.resolvedText ?? data.sampleText ?? 'Sample';
  const fontSize = fitTextToBox({
    text: display || 'Sample',
    width,
    height,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: data.fontStyle,
    maxFontSize: data.maxFontSize,
    minFontSize: data.minFontSize,
  });

  const anchor = resolveTextAnchor(data.textAlign, width);
  // Why: a fabric.Group recomputes its content bbox from its children and
  // re-centers each child within that bbox. With a single text child the bbox
  // collapses to the text itself, so the text always lands at the group center
  // and `originX`/`textAlign` become visually inert (the "Align does nothing"
  // bug). A transparent rect spanning the full placeholder box anchors the bbox
  // to the whole box, so the text keeps its authored left/right edge position —
  // the same reason the badge/label variant carries a full-size background rect.
  const anchorRect = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    fill: 'transparent',
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  const sample = new fabric.Text(display || 'Sample', {
    fontSize,
    fontFamily: data.fontFamily,
    fontWeight: data.fontWeight,
    fontStyle: normalizeFontStyle(data.fontStyle),
    fill: data.fill,
    originX: anchor.originX,
    originY: 'center',
    left: anchor.left,
    top: height / 2,
    textAlign: data.textAlign,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  return assembleGroup([anchorRect, sample], data, geometry, width, height);
}

/**
 * Horizontal anchor for the preview/resolved text block inside the placeholder
 * box. `fabric.Text.textAlign` only justifies lines *within* the text's own
 * bbox — it does not move the block inside the surrounding box, and a group
 * re-centers a lone child. To honor the inspector's Align control we (a) anchor
 * the group bbox to the full box with a transparent rect, and (b) position the
 * text object against the box edge: left → left origin at x=0, right → right
 * origin at x=width, otherwise centered. `textAlign` is still set on the text
 * so multi-line content also aligns its lines. Pure (no Fabric) — unit-testable.
 */
export function resolveTextAnchor(
  textAlign: PlaceholderTextAlign | undefined,
  width: number,
): { originX: 'left' | 'center' | 'right'; left: number } {
  switch (textAlign) {
    case 'left':
      return { originX: 'left', left: 0 };
    case 'right':
      return { originX: 'right', left: width };
    default:
      return { originX: 'center', left: width / 2 };
  }
}

function createImagePreviewLoadingGroup(
  data: PlaceholderImageData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
): fabric.Group {
  const appearance = resolveImageAppearance(data, false);
  const bg = roundedRectShape(width, height, appearance.radii, {
    left: 0,
    top: 0,
    fill: 'rgba(99, 102, 241, 0.04)',
    stroke: appearance.stroke,
    strokeWidth: data.stroke ? appearance.strokeWidth : 1,
    strokeDashArray: appearance.strokeDashArray,
    strokeUniform: true,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  const children: fabric.Object[] = [bg];
  // Why: a fixed-size "loading…" label spills out of a small box. Drop it once
  // the box is too small to carry any chrome (matches the placeholder LOD), and
  // otherwise size it to fit so it never escapes the rect.
  if (selectDetailLevel(width, height) !== 'minimal') {
    const hintText = 'loading…';
    children.push(
      new fabric.Text(hintText, {
        left: width / 2,
        top: height / 2,
        originX: 'center',
        originY: 'center',
        fontSize: fitHintFontSize(hintText, width, height),
        fontFamily: 'Arial',
        fill: HINT_COLOR,
        selectable: false,
        evented: false,
        objectCaching: false,
      }),
    );
  }
  return assembleGroup(children, data, geometry, width, height);
}

export function createImagePreviewGroup(
  data: PlaceholderImageData,
  geometry: PlaceholderGeometry,
  image: fabric.Image,
): fabric.Group {
  const width = Math.max(MIN_RENDER_SIZE, geometry.width);
  const height = Math.max(MIN_RENDER_SIZE, geometry.height);
  const naturalW = (image.getElement() as HTMLImageElement | null)?.naturalWidth || image.width || 1;
  const naturalH = (image.getElement() as HTMLImageElement | null)?.naturalHeight || image.height || 1;

  // Reset fabric.Image to natural dims (clears any prior crop / scale from cache reuse).
  image.set({
    width: naturalW,
    height: naturalH,
    cropX: 0,
    cropY: 0,
  });

  if (data.fitMode === 'cover') {
    // Why: 'cover' must clip to placeholder bounds. Use fabric.Image's built-in
    // cropX/cropY/width/height (in source-pixel space) instead of clipPath —
    // clipPath inside a Group has fragile coordinate semantics.
    const scale = Math.max(width / naturalW, height / naturalH);
    const cropW = width / scale;
    const cropH = height / scale;
    image.set({
      cropX: Math.max(0, (naturalW - cropW) / 2),
      cropY: Math.max(0, (naturalH - cropH) / 2),
      width: cropW,
      height: cropH,
      scaleX: scale,
      scaleY: scale,
    });
  } else {
    const scale = Math.min(width / naturalW, height / naturalH);
    image.set({ scaleX: scale, scaleY: scale });
  }

  image.set({
    left: width / 2,
    top: height / 2,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  const appearance = resolveImageAppearance(data, false);
  const children: fabric.Object[] = [image];
  // Why: a user-set stroke must outline the rendered image. Overlay a
  // borrow-stroke rect on top of the image so the outline tracks the
  // placeholder bounds, not the image bbox after fit-mode scaling.
  if (data.stroke) {
    children.push(
      roundedRectShape(width, height, appearance.radii, {
        left: 0,
        top: 0,
        fill: 'transparent',
        stroke: appearance.stroke,
        strokeWidth: appearance.strokeWidth,
        strokeUniform: true,
        selectable: false,
        evented: false,
        objectCaching: false,
      }),
    );
  }
  return assembleGroup(children, data, geometry, width, height, appearance.radii);
}

export function buildPreviewImageUrl(data: PlaceholderImageData, width: number, height: number): string {
  const seed = previewSeedFor(data);
  const w = Math.max(2, Math.round(width));
  const h = Math.max(2, Math.round(height));
  // Why: synchronous, network-free preview. Picsum (or any external host)
  // can stall or be blocked by CSP/firewalls, leaving the placeholder stuck
  // on "loading…". A data URL always resolves and renders the same image for
  // the same seed/size.
  return buildPreviewDataUrl(seed, w, h);
}

export function previewSeedFor(data: PlaceholderImageData): string {
  const parts: string[] = [];
  if (data.placeholderKey) parts.push(data.placeholderKey);
  if (typeof data.teamNumber === 'number') parts.push(`t${data.teamNumber}`);
  if (typeof data.playerNumber === 'number') parts.push(`p${data.playerNumber}`);
  if (parts.length === 0 && data.previewSeed) parts.push(data.previewSeed);
  if (parts.length === 0) parts.push('placeholder');
  return parts.join('_');
}

function buildPreviewDataUrl(seed: string, width: number, height: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const hash = hashString(seed);
  const hueA = hash % 360;
  const hueB = (hueA + 40 + (hash % 80)) % 360;
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, `hsl(${hueA}, 65%, 62%)`);
  grad.addColorStop(1, `hsl(${hueB}, 55%, 38%)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  ctx.save();
  ctx.globalAlpha = 0.08;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(0, height * 0.65);
  ctx.lineTo(width, height * 0.25);
  ctx.lineTo(width, height * 0.45);
  ctx.lineTo(0, height * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  const baseFontSize = Math.max(12, Math.min(width, height) * 0.16);
  const horizontalPadding = Math.max(4, width * 0.06);
  const maxTextWidth = Math.max(8, width - horizontalPadding * 2);
  const fontSize = fitFontSizeToWidth({
    text: seed,
    maxWidth: maxTextWidth,
    initialFontSize: baseFontSize,
    minFontSize: 8,
    measureAt: (s, size) => {
      ctx.font = `bold ${size}px Arial, sans-serif`;
      return ctx.measureText(s).width;
    },
  });
  ctx.font = `bold ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.88)';
  ctx.fillText(seed, width / 2, height / 2);

  return canvas.toDataURL('image/png');
}

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export interface PlaceholderAppearance {
  stroke: string;
  strokeWidth: number;
  strokeDashArray: number[] | undefined;
  /** Uniform radius (= `cornerRadius`); kept for the all-corners-equal fast path. */
  rx: number;
  ry: number;
  /** Resolved per-corner radii — what the render sites actually draw. */
  radii: PlaceholderCornerRadii;
}

/**
 * Resolves the visual style for an image placeholder. Warning state wins so
 * authors can't hide the invalid cue. Otherwise user-set `stroke` replaces
 * the dashed-indigo default with a solid stroke at the chosen color/width.
 */
export function resolveImageAppearance(
  data: PlaceholderImageData,
  isInvalid: boolean,
): PlaceholderAppearance {
  const radius = data.cornerRadius ?? DEFAULT_IMAGE_CORNER_RADIUS;
  const radii = resolveImageCornerRadii(data);
  if (isInvalid) {
    return {
      stroke: WARNING_OUTLINE_COLOR,
      strokeWidth: STROKE_WIDTH,
      strokeDashArray: DASH_ARRAY,
      rx: radius,
      ry: radius,
      radii,
    };
  }
  if (data.stroke) {
    return {
      stroke: data.stroke,
      strokeWidth: data.strokeWidth ?? STROKE_WIDTH,
      strokeDashArray: undefined,
      rx: radius,
      ry: radius,
      radii,
    };
  }
  return {
    stroke: OUTLINE_COLOR,
    strokeWidth: STROKE_WIDTH,
    strokeDashArray: DASH_ARRAY,
    rx: radius,
    ry: radius,
    radii,
  };
}

function defaultPlaceholderAppearance(isInvalid: boolean): PlaceholderAppearance {
  return {
    stroke: isInvalid ? WARNING_OUTLINE_COLOR : OUTLINE_COLOR,
    strokeWidth: STROKE_WIDTH,
    strokeDashArray: DASH_ARRAY,
    rx: 4,
    ry: 4,
    radii: { tl: 4, tr: 4, br: 4, bl: 4 },
  };
}

function backgroundRect(
  width: number,
  height: number,
  isMissingData: boolean,
  appearance: PlaceholderAppearance,
): fabric.Object {
  return roundedRectShape(width, height, appearance.radii, {
    left: 0,
    top: 0,
    fill: isMissingData ? WARNING_FILL_COLOR : FILL_COLOR,
    stroke: appearance.stroke,
    strokeWidth: appearance.strokeWidth,
    strokeDashArray: appearance.strokeDashArray,
    strokeUniform: true,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function badge(text: string, width: number): fabric.Object[] {
  const natural = text.length * 7 + BADGE_PADDING_X * 2;
  const labelWidth = Math.max(0, Math.min(natural, width - 4));
  const bg = new fabric.Rect({
    left: 0,
    top: 0,
    width: labelWidth,
    height: BADGE_HEIGHT,
    fill: BADGE_BG,
    selectable: false,
    evented: false,
    objectCaching: false,
    rx: 2,
    ry: 2,
  });
  const label = new fabric.Text(text, {
    left: labelWidth / 2,
    top: BADGE_HEIGHT / 2,
    originX: 'center',
    originY: 'center',
    fontSize: 11,
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fill: BADGE_FG,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
  return [bg, label];
}

function labelText(data: PlaceholderData, width: number): fabric.Text {
  const measure = makeLabelMeasure();
  const display = truncateTextToWidth(composeIdentityLabel(data), Math.max(0, width - 12), measure);
  return new fabric.Text(display, {
    left: 6,
    top: BADGE_HEIGHT + 2,
    fontSize: LABEL_FONT_SIZE,
    fontFamily: 'Arial',
    fontWeight: 'normal',
    fill: LABEL_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

let labelMeasureCtx: CanvasRenderingContext2D | null = null;

function makeLabelMeasure(): (s: string) => number {
  if (!labelMeasureCtx) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `normal ${LABEL_FONT_SIZE}px Arial`;
      labelMeasureCtx = ctx;
    }
  }
  const ctx = labelMeasureCtx;
  if (ctx) {
    return (s) => ctx.measureText(s).width;
  }
  // Fallback when no 2D context is available (e.g. unusual headless env).
  const charWidth = LABEL_FONT_SIZE * 0.7;
  return (s) => s.length * charWidth;
}

export interface FitFontSizeOptions {
  text: string;
  maxWidth: number;
  initialFontSize: number;
  minFontSize: number;
  measureAt: (text: string, fontSize: number) => number;
}

export function fitFontSizeToWidth(options: FitFontSizeOptions): number {
  const { text, maxWidth, initialFontSize, minFontSize, measureAt } = options;
  if (!text || maxWidth <= 0) return minFontSize;
  const measured = measureAt(text, initialFontSize);
  if (measured <= maxWidth || measured <= 0) return initialFontSize;
  const scaled = initialFontSize * (maxWidth / measured);
  const clamped = Math.max(minFontSize, Math.min(initialFontSize, scaled));
  return Math.round(clamped * 10) / 10;
}

export function truncateTextToWidth(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
): string {
  if (maxWidth <= 0) return '';
  if (measure(text) <= maxWidth) return text;
  const ellipsis = '…';
  if (measure(ellipsis) > maxWidth) return ellipsis;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (measure(text.slice(0, mid) + ellipsis) <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo === 0 ? ellipsis : text.slice(0, lo) + ellipsis;
}

function composeIdentityLabel(data: PlaceholderData): string {
  const parts: string[] = [];
  const baseKey = data.placeholderKey ? data.placeholderKey : '(unset key)';
  parts.push(baseKey);
  if (typeof data.teamNumber === 'number') parts.push(`team #${data.teamNumber}`);
  if (typeof data.playerNumber === 'number') parts.push(`player #${data.playerNumber}`);
  if (typeof data.matchNumber === 'number') parts.push(`match #${data.matchNumber}`);
  const right = data.placeholderLabel ? ` — ${data.placeholderLabel}` : '';
  return parts.join(' · ') + right;
}

function centerHint(text: string, width: number, height: number): fabric.Text {
  return new fabric.Text(text, {
    left: width / 2,
    top: (height + BADGE_HEIGHT + LABEL_FONT_SIZE) / 2 + 2,
    originX: 'center',
    originY: 'center',
    fontSize: Math.max(12, Math.min(width, height) * 0.18),
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fill: HINT_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

/**
 * Estimated font size that keeps `text` inside a `width`×`height` box on both
 * axes, assuming an Arial-bold average glyph advance of ~0.6em. The group
 * clipPath still guards any residual overflow from font substitution, so a
 * slight over-estimate degrades to a clean crop rather than a spill.
 */
export function fitHintFontSize(text: string, width: number, height: number): number {
  const glyphs = Math.max(1, text.length) * 0.6;
  const byWidth = (width * 0.84) / glyphs;
  const byHeight = height * 0.62;
  return Math.max(7, Math.min(byWidth, byHeight, 24));
}

/**
 * The compact-tier kind hint: a single centered, size-fitted `"TEXT"`/`"IMAGE"`
 * label used when the box is too small for the badge + identity-label stack.
 */
function compactHint(text: string, width: number, height: number): fabric.Text {
  return new fabric.Text(text, {
    left: width / 2,
    top: height / 2,
    originX: 'center',
    originY: 'center',
    fontSize: fitHintFontSize(text, width, height),
    fontFamily: 'Arial',
    fontWeight: 'bold',
    fill: HINT_COLOR,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function assembleGroup(
  children: fabric.Object[],
  data: PlaceholderData,
  geometry: PlaceholderGeometry,
  width: number,
  height: number,
  clipRadii: PlaceholderCornerRadii = ZERO_RADII,
): fabric.Group {
  const opacity = data.kind === 'placeholder-image' ? data.opacity ?? 1 : 1;
  const group = new fabric.Group(children, {
    ...COMMON_GROUP_PROPS,
    left: geometry.left ?? 100,
    top: geometry.top ?? 100,
    angle: geometry.angle ?? 0,
    width,
    height,
    opacity,
  });
  // Why: fabric.Group computes its own width/height from children bbox during
  // construction; force the intended size so subsequent toJSON / refit math is
  // anchored on the visual rect, not the bbox of the children.
  group.set({ width, height });
  // Why: text-width measurement can still under-truncate in real font stacks
  // (substituted fonts, kerning) for very narrow placeholders, leaving the
  // label visually escaping the rectangle. A clipPath sized to the group
  // guarantees no child renders past the placeholder bounds. The rounded
  // clipPath also rounds the resolved/preview image when the author sets a
  // corner radius on an image placeholder.
  group.clipPath = roundedRectShape(width, height, clipRadii, {
    left: 0,
    top: 0,
    originX: 'center',
    originY: 'center',
    absolutePositioned: false,
  });
  group.data = materializePlaceholderData(data);
  group.setCoords();
  return group;
}

/**
 * Materializes placeholder defaults into the `data` object so the persisted
 * `data` is the single source of truth for both the rendered visual and the
 * inspector. The factory must never apply a value (e.g. corner rounding) only
 * at draw time: that makes the canvas show something `data` does not carry, so
 * the inspector reads empty and a save silently drops it. Stamped onto every
 * group in `assembleGroup`, so creating, refitting, or loading a placeholder
 * (the refit runs on `canvas:loaded`) all carry the default forward and old
 * templates pick it up on first load.
 *
 * Only genuinely-optional fields (e.g. `stroke`, whose absence is a meaningful
 * "use the placeholder cue" state) are left undefined — those are explicit
 * tri-states, not hidden defaults.
 */
export function materializePlaceholderData(data: PlaceholderData): PlaceholderData {
  if (data.kind === 'placeholder-image') {
    return { ...data, cornerRadius: data.cornerRadius ?? DEFAULT_IMAGE_CORNER_RADIUS };
  }
  return { ...data, textAlign: data.textAlign ?? 'center' };
}

export function getPlaceholderData(group: fabric.Object | undefined | null): PlaceholderData | undefined {
  const data = (group as fabric.Object | undefined)?.data as PlaceholderData | undefined;
  if (!data) return undefined;
  if (data.kind !== 'placeholder-image' && data.kind !== 'placeholder-text') return undefined;
  return data;
}

export function getScaledSize(group: fabric.Group): { width: number; height: number } {
  const w = (group.width ?? 0) * (group.scaleX ?? 1);
  const h = (group.height ?? 0) * (group.scaleY ?? 1);
  return { width: w, height: h };
}

function normalizeFontStyle(value: string | undefined): '' | 'normal' | 'italic' | 'oblique' {
  if (value === 'italic' || value === 'oblique' || value === 'normal' || value === '') return value;
  return 'normal';
}
